// src/pages/Suggestions.jsx
import React, { useEffect, useMemo, useState } from "react";
import "../suggestions.css";

const API_BASE = "https://textshare-node.rupioo.com";

export default function Suggestions() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    category: "idea",
    message: "",
    website: "", // honeypot
  });
  const [count, setCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", text: "" });

  // Admin mode
  const [isMaster, setIsMaster] = useState(false);

  // List state (admin)
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const limit = 20;
  const [total, setTotal] = useState(0);
  const pages = useMemo(() => Math.ceil(total / limit) || 1, [total]);

  useEffect(() => {
    // simple localStorage toggle
    try {
      setIsMaster(!!localStorage.getItem("masterIdRupioo"));
    } catch {
      setIsMaster(false);
    }
  }, []);

  useEffect(() => {
    if (isMaster) loadPage(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMaster, page]);

  async function loadPage(p) {
    try {
      const res = await fetch(
        `${API_BASE}/suggestions?page=${p}&limit=${limit}`,
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed to load suggestions");
      setList(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setList([]);
      setTotal(0);
      console.warn("list load error:", e.message);
    }
  }

  /* ----------------- Form handlers (public mode) ----------------- */
  function onChange(e) {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (name === "message") setCount(value.length);
  }
  function setStatusMsg(type, text) {
    setStatus({ type, text });
  }
  async function onSubmit(e) {
    e.preventDefault();
    setStatusMsg("", "");
    if (form.website.trim() !== "") return; // honeypot
    if (!form.message.trim() || form.message.trim().length < 5) {
      setStatusMsg("error", "Please enter at least 5 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/suggestions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          subject: form.subject.trim(),
          category: form.category,
          message: form.message.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok)
        throw new Error(data?.error || "Failed to submit suggestion.");

      setStatusMsg("success", data?.message || "Thanks for your suggestion!");
      setForm({
        name: "",
        email: "",
        subject: "",
        category: "idea",
        message: "",
        website: "",
      });
      setCount(0);
    } catch (err) {
      setStatusMsg("error", err.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  }

  /* ----------------- Render ----------------- */
  if (isMaster) {
    return (
      <main className="s-container">
        <header className="s-hero">
          <h1>Suggestions (Admin)</h1>
          <p>
            Showing {limit} per page · Total: {total}
          </p>
        </header>

        <section className="s-card">
          {list.length === 0 ? (
            <div className="s-empty">No suggestions found.</div>
          ) : (
            <div className="s-table-wrap">
              <table className="s-table">
                <thead>
                  <tr>
                    <th>Created</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Subject</th>
                    <th>Category</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((it) => (
                    <tr key={it._id}>
                      <td>{new Date(it.createdAt).toLocaleString()}</td>
                      <td>{it.name || "-"}</td>
                      <td>{it.email || "-"}</td>
                      <td>{it.subject || "-"}</td>
                      <td>{it.category || "-"}</td>
                      <td className="s-pre">{it.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="s-pager">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              ← Prev
            </button>
            <span>
              Page {page} of {pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
            >
              Next →
            </button>
          </div>
        </section>

        <footer className="s-footnote">
          <p>
            This view appears because <code>masterIdRupioo</code> exists in
            localStorage.
          </p>
        </footer>
      </main>
    );
  }

  // Public submit form
  return (
    <main className="s-container">
      <header className="s-hero">
        <h1>Send a Suggestion</h1>
        <p>
          Spotted a bug, have an idea, or want a new feature? Tell us below.
        </p>
      </header>

      <form className="s-card" onSubmit={onSubmit} noValidate>
        <div className="s-row">
          <label htmlFor="name">Name (optional)</label>
          <input
            id="name"
            name="name"
            type="text"
            maxLength={120}
            value={form.name}
            onChange={onChange}
          />
        </div>

        <div className="s-row">
          <label htmlFor="email">Email (optional)</label>
          <input
            id="email"
            name="email"
            type="email"
            maxLength={200}
            value={form.email}
            onChange={onChange}
          />
        </div>

        <div className="s-row">
          <label htmlFor="subject">Subject (optional)</label>
          <input
            id="subject"
            name="subject"
            type="text"
            maxLength={200}
            value={form.subject}
            onChange={onChange}
          />
        </div>

        <div className="s-row">
          <label htmlFor="category">Category</label>
          <select
            id="category"
            name="category"
            value={form.category}
            onChange={onChange}
          >
            <option value="idea">Idea</option>
            <option value="feature">Feature</option>
            <option value="bug">Bug</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="s-row">
          <label htmlFor="message">
            Message <span className="s-req">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            rows={6}
            minLength={5}
            maxLength={5000}
            required
            placeholder="Describe your suggestion..."
            value={form.message}
            onChange={onChange}
          />
          <div className="s-hint">
            <span>{count}</span>/5000
          </div>
        </div>

        {/* Honeypot */}
        <input
          type="text"
          id="website"
          name="website"
          autoComplete="off"
          tabIndex={-1}
          className="s-honeypot"
          value={form.website}
          onChange={onChange}
        />

        <div className="s-actions">
          <button type="submit" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit"}
          </button>
          <button
            type="reset"
            className="s-ghost"
            onClick={() => {
              setForm({
                name: "",
                email: "",
                subject: "",
                category: "idea",
                message: "",
                website: "",
              });
              setCount(0);
              setStatus({ type: "", text: "" });
            }}
          >
            Reset
          </button>
        </div>

        {status.text ? (
          <div className={`s-msg ${status.type}`} aria-live="polite">
            {status.text}
          </div>
        ) : null}
      </form>

      <footer className="s-footnote">
        <p>
          We may store your suggestion to improve the app. Please avoid sharing
          sensitive information.
        </p>
      </footer>
    </main>
  );
}
